import { createContext, type PropsWithChildren, type ReactElement, useCallback, useContext } from 'react';

import { AREA_TAG } from '../observability/areaTag';

/**
 * The structured detail that travels with a report, kept to the subset a slot has anything
 * to say about. Declared here rather than imported from the host's monitoring library, which
 * is the whole point: the shape a slot can describe stays independent of what consumes it.
 */
export interface SlotErrorContext {
    level?: 'error' | 'warning';
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
}

/**
 * Reports one slot misconfiguration to the host's error monitoring.
 *
 * `context` carries the detail a triager needs as structured data rather than folded into
 * the message, so the host decides what shape its own monitoring wants it in.
 */
export type SlotErrorReport = (error: Error, context: SlotErrorContext) => void;

// A no-op default, for the same reason the analytics sink has one: this package renders
// widgets and does not own the product's third-party surface. A host that mounts no provider
// has not wired error monitoring up to slots, which is the host's decision to make.
const noop: SlotErrorReport = () => {};

const SlotErrorReportingContext = createContext<SlotErrorReport>(noop);

export interface SlotErrorReportingProviderProps extends PropsWithChildren {
    reportError: SlotErrorReport;
}

/**
 * Supplies the error sink every slot below it reports misconfiguration to.
 *
 * Injected rather than imported for the same reason as the analytics sink in
 * `../analytics/slotAnalytics`: Sentry lives in `@luminovo/commons` alongside the rest of the
 * product's third-party surface, and a slot primitive has no business depending on any of it.
 * Injection also means a test asserts against a spy instead of mocking a module.
 */
export function SlotErrorReportingProvider({ reportError, children }: SlotErrorReportingProviderProps): ReactElement {
    return <SlotErrorReportingContext.Provider value={reportError}>{children}</SlotErrorReportingContext.Provider>;
}

export function useSlotErrorReporting(): SlotErrorReport {
    const report = useContext(SlotErrorReportingContext);
    // Tagged here rather than at each call site, so a slot cannot report untagged.
    return useCallback(
        (error, context) => report(error, { ...context, tags: { ...AREA_TAG, ...context.tags } }),
        [report],
    );
}
